/* ═══════════════════════════════════════════════════════════════
   INSTINCT FIRST — commerce layer

     1. quickAdd   — size chips + add to cart without leaving the page
     2. countdown  — real deadline timers (no perpetual fake urgency)
     3. stickyCta  — persistent CTA bar once the hero is past
     4. exitOffer  — exit-intent capture, once per visitor per N days
     5. rotator    — cycling promo-bar messages

   All of it is progressive: nothing here hides content, so a failure
   leaves a working, readable page.
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── 1. Quick add ─────────────────────────────────────────── */

  // Cart adds from a card are deliberately quiet: no drawer, no page
  // change. The customer confirmed on the card and keeps browsing.
  // Bundled section rendering gives back the re-rendered cart markup, so
  // the badge and the drawer's contents stay correct without opening it.
  function addToCart(variantId, qty) {
    var url = (window.Shopify && Shopify.routes && Shopify.routes.cart_add_url) || '/cart/add';
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: qty || 1 }],
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        // A non-JSON body (proxy error page, network appliance) must not
        // become a parse exception that hides the real failure.
        return r
          .json()
          .catch(function () { return {}; })
          .then(function (data) {
            if (!r.ok) {
              var err = new Error(cartErrorText(data, r.status) || 'cart add failed');
              err.userMessage = cartErrorText(data, r.status);
              throw err;
            }
            return data;
          });
      })
      .then(function (data) {
        applyCartSections(data.sections);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }));
        return data;
      });
  }

  // Swap in the freshly rendered cart markup. Mirrors what the theme's own
  // add-to-cart does, minus the part that opens the drawer.
  function applyCartSections(sections) {
    if (!sections) return;
    if (typeof window.updateAllCartElements === 'function') {
      window.updateAllCartElements(sections);
      return;
    }
    Object.keys(sections).forEach(function (key) {
      var doc = new DOMParser().parseFromString(sections[key], 'text/html');
      if (key === 'cart-count-badge') {
        var fresh = doc.querySelector('.cart-count-badge');
        if (!fresh) return;
        document.querySelectorAll('.cart-count-badge').forEach(function (el) {
          el.replaceWith(fresh.cloneNode(true));
        });
      } else if (key === 'offcanvas-cart') {
        var freshCart = doc.querySelector('cart-container, .cart-container');
        if (!freshCart) return;
        document.querySelectorAll('cart-container, .cart-container').forEach(function (el) {
          el.replaceWith(freshCart.cloneNode(true));
        });
      }
    });
    window.dispatchEvent(new CustomEvent('kt.cart.reloaded'));
  }

  function openCartDrawer() {
    var drawer = document.getElementById('offcanvas-cart');
    if (!drawer) return;
    if (typeof window.showCartDrawer === 'function') { window.showCartDrawer(); return; }
    if (window.bootstrap && window.bootstrap.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
    }
  }

  // Shopify explains a refused add in `description` — "You can only add 2
  // of this item to your cart", "All 3 are in your cart". That is exactly
  // what the customer needs, so pass it through rather than replacing it
  // with a generic retry prompt.
  function cartErrorText(data, status) {
    var d = data || {};
    var msg = d.description || d.message || '';

    // Form-style errors arrive as { quantity: ["..."] }.
    if (d.errors && typeof d.errors === 'object') {
      var parts = [];
      Object.keys(d.errors).forEach(function (k) {
        var val = d.errors[k];
        parts.push(Array.isArray(val) ? val.join(' ') : String(val));
      });
      if (parts.length) msg = parts.join(' ');
    }

    // "Cart Error" is Shopify's internal title, not an explanation.
    if (msg === 'Cart Error') msg = d.description || '';

    if (!msg) {
      if (status === 422) msg = 'That size just went out of stock.';
      else if (status === 404) msg = 'This product is no longer available.';
      else if (!navigator.onLine) msg = 'You appear to be offline.';
    }
    return msg;
  }

  function fallbackCartError() {
    return (
      (document.body && document.body.getAttribute('data-if-cart-error')) ||
      "Couldn't add this to your cart. Please try again."
    );
  }

  // A message placed next to the button that failed, announced to screen
  // readers. One per button, reused, so repeated failures do not stack up.
  function showCartMessage(btn, text) {
    if (!btn || !btn.parentNode) return;
    var el = btn.parentNode.querySelector(':scope > .if-cart-msg');
    if (!el) {
      el = document.createElement('p');
      el.className = 'if-cart-msg';
      el.setAttribute('role', 'alert');
      btn.insertAdjacentElement('afterend', el);
    }
    el.textContent = text;
    el.hidden = false;
  }

  function clearCartMessage(btn) {
    if (!btn || !btn.parentNode) return;
    var el = btn.parentNode.querySelector(':scope > .if-cart-msg');
    if (el) el.hidden = true;
  }

  function initQuickAdd(root) {
    var cards = (root || document).querySelectorAll('[data-if-quick]:not([data-if-bound])');

    cards.forEach(function (box) {
      box.setAttribute('data-if-bound', '');

      var card = box.closest('.if-pcard');
      var sizes = box.querySelectorAll('[data-if-size]');
      var addBtn = box.querySelector('[data-if-add]');
      if (!addBtn) return;

      var addedText = box.getAttribute('data-if-added-text') || 'Added to cart';
      var openDrawer = box.getAttribute('data-if-open-drawer') === 'true';
      var idleText = addBtn.textContent.trim();

      sizes.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          // The whole card image is a link to the PDP. Without this the
          // click bubbles out of the picker and navigates away mid-choice.
          e.preventDefault();
          e.stopPropagation();
          if (btn.disabled) return;
          sizes.forEach(function (b) {
            b.classList.remove('is-selected');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          addBtn.setAttribute('data-variant', btn.getAttribute('data-variant'));
          addBtn.disabled = false;
          addBtn.textContent = 'Add to cart';
        });
      });

      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var id = addBtn.getAttribute('data-variant');
        if (!id || addBtn.disabled) return;

        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addBtn.textContent = 'Adding…';
        clearCartMessage(addBtn);

        addToCart(id, 1)
          .then(function () {
            addBtn.classList.remove('is-loading');
            addBtn.classList.add('is-done');
            addBtn.textContent = addedText;
            if (openDrawer) openCartDrawer();
            setTimeout(function () {
              addBtn.classList.remove('is-done');
              addBtn.textContent = idleText === 'Select a size' ? 'Add to cart' : idleText;
              addBtn.disabled = false;
              if (card) card.classList.remove('is-picking');
            }, 1800);
          })
          .catch(function (err) {
            addBtn.classList.remove('is-loading');
            addBtn.textContent = 'Try again';
            addBtn.disabled = false;
            showCartMessage(addBtn, err.userMessage || fallbackCartError());
          });
      });

      // A click anywhere inside the picker is picker business, never the
      // product link's.
      box.addEventListener('click', function (e) { e.stopPropagation(); });
    });
  }

  // The "+" is the only way in: press it to open the size picker (the
  // button rotates into a "×" and moves clear of the buttons below),
  // press it again to close.
  function initQuickToggles(root) {
    var toggles = (root || document).querySelectorAll('[data-if-quick-toggle]:not([data-if-bound])');
    toggles.forEach(function (btn) {
      btn.setAttribute('data-if-bound', '');
      var card = btn.closest('.if-pcard');
      if (!card) return;
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var opening = !card.classList.contains('is-picking');
        // Only one picker open at a time.
        document.querySelectorAll('.if-pcard.is-picking').forEach(function (c) {
          if (c !== card) closePicker(c);
        });
        card.classList.toggle('is-picking', opening);
        btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        btn.setAttribute('aria-label', opening ? 'Close size picker' : 'Add to cart');
      });
    });

    if (!document.documentElement.hasAttribute('data-if-pick-bound')) {
      document.documentElement.setAttribute('data-if-pick-bound', '');
      // Tapping anywhere else closes the open picker.
      document.addEventListener('click', function (e) {
        document.querySelectorAll('.if-pcard.is-picking').forEach(function (c) {
          if (!c.contains(e.target)) closePicker(c);
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        document.querySelectorAll('.if-pcard.is-picking').forEach(closePicker);
      });
    }
  }

  function closePicker(card) {
    card.classList.remove('is-picking');
    var t = card.querySelector('[data-if-quick-toggle]');
    if (t) {
      t.setAttribute('aria-expanded', 'false');
      t.setAttribute('aria-label', 'Add to cart');
    }
  }

  /* ── Product page ─────────────────────────────────────────── */

  function initProduct(root) {
    var panes = (root || document).querySelectorAll('[data-if-pdp]:not([data-if-bound])');

    panes.forEach(function (pane) {
      pane.setAttribute('data-if-bound', '');

      var sizes = pane.querySelectorAll('[data-if-pdp-size]');
      var addBtn = pane.querySelector('[data-if-pdp-add]');
      var buyBtn = pane.querySelector('[data-if-pdp-buy]');
      var priceOut = pane.querySelector('[data-if-pdp-current]');

      sizes.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          sizes.forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          if (addBtn) {
            addBtn.setAttribute('data-variant', btn.getAttribute('data-variant'));
            addBtn.disabled = false;
          }
          if (buyBtn) {
            buyBtn.setAttribute('data-variant', btn.getAttribute('data-variant'));
            buyBtn.disabled = false;
          }
          var price = btn.getAttribute('data-price');
          if (price && priceOut) priceOut.textContent = price;
          // The sticky bar carries its own copy of the picker; keep the
          // two in step so whichever one is on screen is the right one.
          document.dispatchEvent(new CustomEvent('if:variant', {
            detail: { id: btn.getAttribute('data-variant'), price: price, from: 'pdp' },
          }));
          // Keep the URL in step, so a shared link opens the same size.
          var id = btn.getAttribute('data-variant');
          if (id && window.history && history.replaceState) {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', id);
            history.replaceState({}, '', u);
          }
        });
      });

      // An on-site checkout (GoKwik) claims Buy-now buttons by their label
      // and runs its own flow. Navigating to /checkout ourselves bypasses
      // it and drops the customer on the native payment page — which is
      // exactly what "Buy now goes to Razorpay" was. Leave the button
      // untouched so the SDK can claim it.
      var occActive = !!(
        window.gokwikSdk ||
        (window.gkBtnConfig && window.gkBtnConfig.buyNowEnabled !== false) ||
        (window.merchantInfo && window.merchantInfo.mid)
      );

      if (buyBtn && occActive) {
        buyBtn.setAttribute('data-if-occ', 'true');
      }

      if (buyBtn && !occActive) {
        buyBtn.addEventListener('click', function () {
          var id = buyBtn.getAttribute('data-variant');
          if (!id || buyBtn.disabled) return;
          var original = buyBtn.textContent;
          buyBtn.disabled = true;
          buyBtn.textContent = 'One moment…';
          addToCart(id, 1)
            .then(function () {
              window.location.href = '/checkout';
            })
            .catch(function (err) {
              // Leave them on the page with a working button rather than
              // bouncing to a checkout that has nothing in it — and say why.
              buyBtn.textContent = original;
              buyBtn.disabled = false;
              showCartMessage(buyBtn, err.userMessage || fallbackCartError());
            });
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var id = addBtn.getAttribute('data-variant');
          if (!id || addBtn.disabled) return;
          var original = addBtn.textContent;
          addBtn.disabled = true;
          addBtn.textContent = 'Adding…';
          clearCartMessage(addBtn);
          addToCart(id, 1)
            .then(function () {
              addBtn.classList.add('is-done');
              addBtn.textContent = 'Added ✓';
              setTimeout(function () {
                addBtn.classList.remove('is-done');
                addBtn.textContent = original;
                addBtn.disabled = false;
              }, 1800);
            })
            .catch(function (err) {
              addBtn.textContent = 'Try again';
              addBtn.disabled = false;
              showCartMessage(addBtn, err.userMessage || fallbackCartError());
            });
        });
      }

      pane.querySelectorAll('[data-if-pdp-q]').forEach(function (q) {
        q.addEventListener('click', function () {
          var row = q.closest('.if-pdp__row');
          if (row) row.classList.toggle('is-open');
        });
      });
    });
  }

  /* ── Sticky offset ────────────────────────────────────────── */

  // Everything pinned on the page sits below the header, and the header
  // hides and re-shows as you scroll. Publish its real on-screen bottom
  // edge so CSS can track it instead of assuming a fixed height — which
  // left a header-sized band of dead space above each pinned column once
  // the header had retracted.
  function initStickyOffset() {
    var header = document.querySelector('#header-group');
    if (!header) return;
    var root = document.documentElement;
    var queued = false;

    function measure() {
      queued = false;
      var gap = parseFloat(getComputedStyle(root).getPropertyValue('--if-canvas-gap')) || 0;
      var bottom = header.getBoundingClientRect().bottom;
      // Negative once the header has animated off the top of the screen.
      if (!(bottom > 0)) bottom = 0;
      root.style.setProperty('--if-sticky-top', bottom + gap + 'px');
    }

    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    window.addEventListener('load', measure);
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(header);
  }

  /* ── Product page: pin a short media column ───────────────── */

  // Sticky can only be applied while the column actually fits on screen.
  // Pinning something taller than the viewport pins its top and puts the
  // rest permanently out of reach, so the class is toggled by measurement
  // rather than set in CSS.
  function initPdpPin(root) {
    var cols = (root || document).querySelectorAll('[data-if-pdp-pin]:not([data-if-pin-bound])');

    cols.forEach(function (col) {
      col.setAttribute('data-if-pin-bound', '');
      var queued = false;

      function measure() {
        queued = false;

        // Below 1000px the media column is a horizontal scroller, not a
        // column, and the buy panel is in plain flow.
        if (window.innerWidth < 1000) {
          col.classList.remove('is-pinned');
          col.style.removeProperty('--if-pin-over');
          return;
        }

        var cs = getComputedStyle(document.documentElement);
        // Measured against the header at REST, not its live position. The
        // live offset changes as the header hides, and feeding that back
        // in here would make the pinned column drift while scrolling.
        var headerH = parseFloat(cs.getPropertyValue('--if-header-h')) || 100;
        var canvasGap = parseFloat(cs.getPropertyValue('--if-canvas-gap')) || 0;
        var gap = parseFloat(col.getAttribute('data-pin-gap')) || 0;
        var room = window.innerHeight - headerH - canvasGap - gap;

        // How much taller than the screen this column is. CSS subtracts it
        // from the sticky offset, so a tall column scrolls until its bottom
        // edge is in view and pins there; a short one gets 0 and simply
        // pins below the header.
        var over = col.scrollHeight - room;
        if (!(over > 0)) over = 0;

        col.style.setProperty('--if-pin-over', over + 'px');
        col.classList.add('is-pinned');
      }

      // rAF-debounced: setting the variable and the class changes the
      // column's own box, which would otherwise feed straight back into
      // the ResizeObserver.
      function queue() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(measure);
      }

      measure();
      window.addEventListener('resize', queue);
      window.addEventListener('load', queue);
      // Images land late and change the column's height.
      if ('ResizeObserver' in window) new ResizeObserver(queue).observe(col);
    });
  }

  /* ── Product image zoom ──────────────────────────────────── */

  // GLightbox ships with the theme and is loaded by the section when zoom
  // is on. It is a deferred script, so it may not be defined yet when this
  // runs — poll briefly rather than binding to window.load, which can be
  // long past by the time a section re-renders in the theme editor.
  function initZoom(root) {
    var links = (root || document).querySelectorAll('.if-pdp__zoom:not([data-if-zoom-bound])');
    if (!links.length) return;
    links.forEach(function (a) { a.setAttribute('data-if-zoom-bound', ''); });

    var tries = 0;
    (function ready() {
      if (typeof window.GLightbox === 'function') {
        window.GLightbox({
          selector: '.if-pdp__zoom',
          touchNavigation: true,
          keyboardNavigation: true,
          loop: true,
          zoomable: true,
          draggable: true,
          openEffect: 'fade',
          closeEffect: 'fade',
          slideEffect: 'slide',
        });
        return;
      }
      // ~5s of 100ms checks, then give up: the links stay plain anchors to
      // the full-size image, which is a working fallback.
      if (tries++ < 50) setTimeout(ready, 100);
    })();
  }

  /* ── Discount code ───────────────────────────────────────── */

  // /discount/<code> registers the code against the cart server-side. The
  // theme then sent the customer to /checkout to see the result; instead
  // re-render the drawer, which already knows how to display
  // cart_level_discount_applications and the discounted total.
  function initCartDiscount(root) {
    var boxes = (root || document).querySelectorAll('[data-if-discount]:not([data-if-bound])');

    boxes.forEach(function (box) {
      box.setAttribute('data-if-bound', '');
      var input = box.querySelector('[data-if-discount-input]');
      var btn = box.querySelector('[data-if-discount-apply]');
      if (!input || !btn) return;

      // Looked up live, never closed over: painting the drawer replaces
      // these nodes, and writing to the old detached one would silently
      // say nothing at all.
      function say(text, ok) {
        var msg = document.querySelector('[data-if-discount-msg]');
        if (!msg) return;
        msg.textContent = text;
        msg.hidden = !text;
        msg.classList.toggle('is-ok', !!ok);
        msg.classList.toggle('is-bad', !ok);
      }

      // Swap in the freshly rendered drawer, then put back everything the
      // customer had going: bindings, the open panel, what they typed.
      function paint(sections) {
        var typed = input.value;
        applyCartSections(sections);
        initCartDiscount(document);
        initDiscountRemove(document);
        syncDiscounts();

        var panel = document.getElementById('cart-discount-collapse');
        if (panel && window.bootstrap && window.bootstrap.Collapse) {
          window.bootstrap.Collapse.getOrCreateInstance(panel, { toggle: false }).show();
        }
        var fresh = document.querySelector('[data-if-discount-input]');
        if (fresh) fresh.value = typed;
      }

      function apply() {
        var code = (input.value || '').trim();
        if (!code) return;

        btn.disabled = true;
        btn.classList.add('loading');
        say('', true);

        // One atomic call. The previous version hit /discount/<code> and
        // then read /cart.js separately, and the cart had not always
        // caught up by the second request — which is why the first apply
        // reported failure and the same code worked after a refresh.
        // /cart/update.js sets the code AND returns the updated cart, with
        // the re-rendered drawer, in a single response.
        var url = (window.Shopify && Shopify.routes && Shopify.routes.cart_update_url) || '/cart/update';
        fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            discount: code,
            sections: ['cart-count-badge', 'offcanvas-cart'],
            sections_url: window.location.pathname,
          }),
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var hit = (cart.discount_codes || []).filter(function (d) {
              return String(d.code).toLowerCase() === code.toLowerCase();
            })[0];

            if (!hit) {
              say('That code is not valid.', false);
              btn.disabled = false;
              btn.classList.remove('loading');
              return;
            }

            var ok = hit.applicable !== false;
            // A code Shopify accepted but cannot apply still lands on the
            // cart, so render either way — it has to be visible to be
            // removable.
            paint(cart.sections);
            say(ok ? 'Discount applied.' : 'That code does not apply to this cart.', ok);
          })
          .catch(function () {
            say('Could not apply that code. Please try again.', false);
            btn.disabled = false;
            btn.classList.remove('loading');
          });
      }

      btn.addEventListener('click', apply);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        }
      });
    });
  }

  // Every discount chip is rendered from /cart.js, not from Liquid.
  //
  // Liquid only exposes cart_level_discount_applications — an ORDER-level
  // discount. A code that discounts specific products lands on the line
  // items and never appears there, and a code Shopify accepted but could
  // not apply (applicable:false) is invisible to Liquid entirely. The Ajax
  // cart reports all three, so it is the only source that can show the
  // customer what is actually on their cart.
  function syncDiscounts() {
    var list = document.querySelector('[data-if-discounts]');
    if (!list) return;

    var root_url = (window.Shopify && Shopify.routes && Shopify.routes.root_url) || '/';
    fetch(root_url + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Only codes that are actually discounting something get a row.
        // A code Shopify accepted but cannot apply yet is left ON the cart
        // deliberately — a minimum-spend code should start working once the
        // customer adds enough — but it needs no row, because the message
        // under the field already said so.
        var codes = (cart.discount_codes || []).filter(function (d) {
          return d.applicable !== false;
        });
        list.innerHTML = '';

        codes.forEach(function (d) {
          var li = document.createElement('li');
          li.className = 'cart-discount-row';

          var label = document.createElement('span');
          label.className = 'cart-discount-row__label';
          label.textContent = 'Discount';

          var code = document.createElement('b');
          code.className = 'cart-discount-row__code';
          code.textContent = d.code;
          label.appendChild(code);

          var amount = document.createElement('span');
          amount.className = 'cart-discount-row__amount';
          // total_discount covers order- and line-level alike. The online
          // store allows one code at a time, so it is this code's saving.
          amount.textContent = cart.total_discount > 0
            ? '-' + money(cart.total_discount, cart.currency)
            : '';

          var x = document.createElement('button');
          x.type = 'button';
          x.className = 'cart-discount-row__remove';
          x.setAttribute('data-if-discount-remove', d.code);
          x.setAttribute('aria-label', 'Remove discount ' + d.code);
          x.innerHTML = '&times;';

          li.appendChild(label);
          li.appendChild(amount);
          li.appendChild(x);
          list.appendChild(li);
        });

        list.hidden = codes.length === 0;
        initDiscountRemove(document);
      })
      .catch(function () {});
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'INR',
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (err) {
      return (cents / 100).toFixed(2);
    }
  }

  // The online store cart holds one discount code at a time, so clearing
  // is a single call. Verified against the live cart: POST /cart/update.js
  // with an empty `discount` empties cart.discount_codes.
  function initDiscountRemove(root) {
    var btns = (root || document).querySelectorAll('[data-if-discount-remove]:not([data-if-bound])');

    btns.forEach(function (btn) {
      btn.setAttribute('data-if-bound', '');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        btn.disabled = true;
        var url = (window.Shopify && Shopify.routes && Shopify.routes.cart_update_url) || '/cart/update';
        fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ discount: '' }),
        })
          .then(refreshCart)
          .catch(function () { btn.disabled = false; });
      });
    });
  }

  // A no-op cart update is the cheapest way to get the sections back
  // re-rendered with the discount included.
  function refreshCart() {
    var url = (window.Shopify && Shopify.routes && Shopify.routes.cart_update_url) || '/cart/update';
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        attributes: {},
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        applyCartSections(data.sections);
        // The drawer markup was replaced, so re-bind and re-open the
        // discount panel the customer was just using.
        initCartDiscount(document);
        initDiscountRemove(document);
        var panel = document.getElementById('cart-discount-collapse');
        if (panel && window.bootstrap && window.bootstrap.Collapse) {
          window.bootstrap.Collapse.getOrCreateInstance(panel, { toggle: false }).show();
        }
        return data;
      });
  }

  /* ── Card image peek (touch) ─────────────────────────────── */

  // Hover swaps to the second product shot, which a touch screen has no
  // way to ask for. Press and hold does the same job: hold to see the
  // alternate image, lift to go back.
  //
  // A short delay before it engages, so a normal tap-to-open does not
  // flash the second image on its way to the product page, and any real
  // movement cancels it so scrolling is never hijacked.
  function initCardPeek(root) {
    var cards = (root || document).querySelectorAll('[data-if-card]:not([data-if-peek-bound])');

    cards.forEach(function (card) {
      card.setAttribute('data-if-peek-bound', '');
      if (!card.querySelector('.if-pcard__img--alt')) return;

      var timer = null;
      var startX = 0;
      var startY = 0;

      function stop() {
        clearTimeout(timer);
        card.classList.remove('is-peeking');
      }

      card.addEventListener(
        'touchstart',
        function (e) {
          // Buttons on the card do their own thing — quick add, wishlist.
          if (e.target.closest('button, [data-if-quick], wishlist-button')) return;
          var t = e.touches[0];
          startX = t.clientX;
          startY = t.clientY;
          timer = setTimeout(function () {
            card.classList.add('is-peeking');
          }, 140);
        },
        { passive: true }
      );

      card.addEventListener(
        'touchmove',
        function (e) {
          var t = e.touches[0];
          if (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8) stop();
        },
        { passive: true }
      );

      card.addEventListener('touchend', stop);
      card.addEventListener('touchcancel', stop);
    });
  }

  /* ── Quick view ──────────────────────────────────────────── */

  // The quick view fetches the product page, parses it with DOMParser and
  // moves the buy column into its dialog. Scripts that arrive that way are
  // INERT: a DOMParser document has scripting disabled, and adopting a
  // script element into the live page does not re-arm it. So every block
  // that ships its own <script> — the size chart, and any custom Liquid
  // block — rendered but did nothing.
  //
  // Re-creating each script element is what makes the browser run it.
  function runInjectedScripts(root) {
    root.querySelectorAll('script:not([data-if-ran])').forEach(function (old) {
      var fresh = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        fresh.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      fresh.setAttribute('data-if-ran', '');
      fresh.textContent = old.textContent;
      try {
        old.replaceWith(fresh);
      } catch (err) {
        // One bad block must not stop the rest from running.
        if (window.console) console.warn('[instinct-first/quick-view]', err);
      }
    });
  }

  function initQuickView() {
    if (document.documentElement.hasAttribute('data-if-qv-bound')) return;
    document.documentElement.setAttribute('data-if-qv-bound', '');

    window.addEventListener('kt.product.quick_view.modal_shown', function () {
      var modal = document.getElementById('quick-view-modal');
      if (!modal) return;
      runInjectedScripts(modal);
      // Re-run the per-subtree initialisers over the new content. NOT
      // boot(): that also wires page-level scroll and resize listeners,
      // which would stack up another set every time the dialog opens.
      try {
        initQuickAdd(modal);
        initQuickToggles(modal);
        initProduct(modal);
        initZoom(modal);
        initVideos(modal);
        initNotify(modal);
        initCountdowns(modal);
        initRotators(modal);
      } catch (err) {
        if (window.console) console.error('[instinct-first/quick-view]', err);
      }
    });
  }

  /* ── Sticky add to cart ───────────────────────────────────── */

  // Desktop and mobile are configured independently in the section, so
  // this reads the side that currently applies and re-reads it on resize.
  function initStickyAtc(root) {
    var bars = (root || document).querySelectorAll('[data-if-sticky]:not([data-if-bound])');

    bars.forEach(function (bar) {
      bar.setAttribute('data-if-bound', '');

      var addBtn = bar.querySelector('[data-if-sticky-add]');
      var priceOut = bar.querySelector('[data-if-sticky-price]');
      var sizes = bar.querySelectorAll('[data-if-sticky-size]');
      if (!addBtn) return;

      var breakpoint = parseInt(bar.getAttribute('data-breakpoint'), 10) || 768;
      var addedText = bar.getAttribute('data-added-text') || 'Added to cart';
      var openDrawer = bar.getAttribute('data-open-drawer') === 'true';
      var hideInFooter = bar.getAttribute('data-hide-in-footer') === 'true';
      var idleText = addBtn.textContent.trim();

      function side() {
        var mobile = window.innerWidth <= breakpoint;
        return {
          enabled: bar.getAttribute(mobile ? 'data-mobile-enable' : 'data-desktop-enable') === 'true',
          trigger: bar.getAttribute(mobile ? 'data-mobile-trigger' : 'data-desktop-trigger') || 'always',
          offset: parseInt(bar.getAttribute(mobile ? 'data-mobile-offset' : 'data-desktop-offset'), 10) || 0,
        };
      }

      /* ── selection ── */

      function select(id, price) {
        if (!id) return;
        addBtn.setAttribute('data-variant', id);
        addBtn.disabled = false;
        if (price && priceOut) priceOut.textContent = price;
        sizes.forEach(function (b) {
          b.classList.toggle('is-selected', b.getAttribute('data-variant') === String(id));
        });
      }

      sizes.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (btn.disabled) return;
          select(btn.getAttribute('data-variant'), btn.getAttribute('data-price'));
          // Mirror the choice back onto the product page's own picker.
          document.querySelectorAll('[data-if-pdp-size]').forEach(function (p) {
            if (p.getAttribute('data-variant') === btn.getAttribute('data-variant') && !p.disabled) p.click();
          });
        });
      });

      document.addEventListener('if:variant', function (e) {
        if (!e.detail || e.detail.from !== 'pdp') return;
        select(e.detail.id, e.detail.price);
      });

      /* ── add ── */

      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = addBtn.getAttribute('data-variant');
        if (!id || addBtn.disabled) return;

        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        clearCartMessage(addBtn);

        addToCart(id, 1)
          .then(function () {
            addBtn.classList.add('is-done');
            addBtn.textContent = addedText;
            if (openDrawer) openCartDrawer();
            setTimeout(function () {
              addBtn.classList.remove('is-done');
              addBtn.textContent = idleText;
              addBtn.disabled = false;
            }, 1800);
          })
          .catch(function (err) {
            addBtn.textContent = 'Try again';
            addBtn.disabled = false;
            showCartMessage(addBtn, err.userMessage || fallbackCartError());
          });
      });

      /* ── visibility ── */

      // The real add-to-cart button, whichever layout rendered it.
      var anchorEl = document.querySelector('[data-if-pdp-add]')
        || document.querySelector('.if-pdp__atc')
        || document.querySelector('product-form .btn-atc');
      var footer = document.querySelector('#footer-group');

      var anchorVisible = false;
      var footerVisible = false;

      if ('IntersectionObserver' in window) {
        if (anchorEl) {
          new IntersectionObserver(function (entries) {
            anchorVisible = entries[0].isIntersecting;
            update();
          }, { rootMargin: '0px 0px -10% 0px' }).observe(anchorEl);
        }
        if (footer && hideInFooter) {
          new IntersectionObserver(function (entries) {
            footerVisible = entries[0].isIntersecting;
            update();
          }).observe(footer);
        }
      }

      function shouldShow() {
        var cfg = side();
        if (!cfg.enabled) return false;
        if (hideInFooter && footerVisible) return false;
        if (cfg.trigger === 'always') return true;
        if (cfg.trigger === 'scroll') return window.scrollY > cfg.offset;
        // 'atc' — show once the real button has left the viewport. With no
        // button to watch (or no observer), fall back to a scroll distance
        // so the bar is never permanently stuck off screen.
        if (!anchorEl || !('IntersectionObserver' in window)) return window.scrollY > cfg.offset;
        return !anchorVisible;
      }

      function update() {
        bar.classList.toggle('is-shown', shouldShow());
      }

      // `is-live` flips display:none off only once JS is running, so the
      // bar can never sit over the page as a dead element.
      bar.classList.add('is-live');
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      window.addEventListener('load', update);
    });
  }

  /* ── Shoppable video ──────────────────────────────────────── */

  // Autoplay only what's on screen. A row of simultaneously playing
  // videos is the fastest way to flatten a phone battery.
  function initVideos(root) {
    var wraps = (root || document).querySelectorAll('[data-if-video]:not([data-if-bound])');

    wraps.forEach(function (wrap) {
      wrap.setAttribute('data-if-bound', '');
      var vid = wrap.querySelector('video');
      if (!vid) return;

      vid.muted = true;
      vid.setAttribute('playsinline', '');

      if ('IntersectionObserver' in window && !reduced.matches) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              var play = vid.play();
              if (play && play.catch) play.catch(function () {});
            } else {
              vid.pause();
            }
          });
        }, { threshold: 0.4 }).observe(wrap);
      }

      var mute = wrap.querySelector('[data-if-video-mute]');
      if (mute) {
        mute.addEventListener('click', function (e) {
          e.preventDefault();
          vid.muted = !vid.muted;
          mute.textContent = vid.muted ? '\u266A' : '\u25B6';
          mute.setAttribute('aria-pressed', vid.muted ? 'false' : 'true');
        });
      }
    });
  }

  /* ── Back-in-stock request ────────────────────────────────── */

  function initNotify(root) {
    var buttons = (root || document).querySelectorAll('[data-if-notify]:not([data-if-bound])');
    var modal = document.querySelector('[data-if-notify-modal]');
    if (!modal) return;

    var nameOut = modal.querySelector('[data-if-notify-product]');
    var field = modal.querySelector('[data-if-notify-field]');

    buttons.forEach(function (btn) {
      btn.setAttribute('data-if-bound', '');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var title = btn.getAttribute('data-product') || '';
        if (nameOut) nameOut.textContent = title;
        // Recorded as customer tags so the request is segmentable.
        if (field) field.value = 'notify-me, notify:' + (btn.getAttribute('data-handle') || '');
        modal.classList.add('is-open');
        var input = modal.querySelector('input[type="email"]');
        if (input) input.focus();
      });
    });

    if (!modal.hasAttribute('data-if-bound')) {
      modal.setAttribute('data-if-bound', '');
      modal.addEventListener('click', function (e) {
        if (e.target === modal || e.target.closest('[data-if-notify-close]')) {
          modal.classList.remove('is-open');
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') modal.classList.remove('is-open');
      });
    }
  }

  /* ── 2. Countdown ─────────────────────────────────────────── */

  function initCountdowns(root) {
    var els = (root || document).querySelectorAll('[data-if-countdown]:not([data-if-bound])');

    els.forEach(function (el) {
      el.setAttribute('data-if-bound', '');
      var target = Date.parse(el.getAttribute('data-if-countdown'));
      if (isNaN(target)) {
        el.setAttribute('data-if-expired', '');
        return;
      }

      var out = {
        d: el.querySelector('[data-cd-d]'),
        h: el.querySelector('[data-cd-h]'),
        m: el.querySelector('[data-cd-m]'),
        s: el.querySelector('[data-cd-s]'),
      };

      function pad(n) { return n < 10 ? '0' + n : '' + n; }

      function tick() {
        var left = target - Date.now();
        if (left <= 0) {
          el.setAttribute('data-if-expired', '');
          clearInterval(timer);
          return;
        }
        var s = Math.floor(left / 1000);
        if (out.d) out.d.textContent = pad(Math.floor(s / 86400));
        if (out.h) out.h.textContent = pad(Math.floor((s % 86400) / 3600));
        if (out.m) out.m.textContent = pad(Math.floor((s % 3600) / 60));
        if (out.s) out.s.textContent = pad(s % 60);
      }

      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* ── 3. Sticky CTA ────────────────────────────────────────── */

  function initStickyCta(root) {
    var bars = (root || document).querySelectorAll('[data-if-sticky]:not([data-if-bound])');

    bars.forEach(function (bar) {
      bar.setAttribute('data-if-bound', '');
      var after = parseInt(bar.getAttribute('data-if-sticky-after'), 10) || 700;
      var queued = false;

      function update() {
        queued = false;
        bar.classList.toggle('is-visible', window.scrollY > after);
      }
      window.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
      }, { passive: true });
      update();

      var close = bar.querySelector('[data-if-sticky-close]');
      if (close) {
        close.addEventListener('click', function () {
          bar.setAttribute('hidden', '');
        });
      }
    });
  }

  /* ── 4. Exit intent ───────────────────────────────────────── */

  function store(key, days) {
    try {
      var raw = localStorage.getItem(key);
      if (raw && Date.now() - Number(raw) < days * 86400000) return false;
      localStorage.setItem(key, String(Date.now()));
      return true;
    } catch (e) {
      return false; // private mode: never nag
    }
  }

  function initExitOffer(root) {
    var modals = (root || document).querySelectorAll('[data-if-exit]:not([data-if-bound])');

    modals.forEach(function (modal) {
      modal.setAttribute('data-if-bound', '');
      var days = parseInt(modal.getAttribute('data-if-exit-days'), 10) || 7;
      var key = 'if-exit-offer';
      var shown = false;

      function open() {
        if (shown) return;
        if (!store(key, days)) return;
        shown = true;
        modal.classList.add('is-open');
        var f = modal.querySelector('input[type="email"]');
        if (f) f.focus();
      }

      function close() {
        modal.classList.remove('is-open');
      }

      // Never fire in the first few seconds — an offer that appears
      // before the page is read is just an obstruction.
      var armedAt = Date.now() + 12000;
      function armed() { return Date.now() > armedAt; }

      // Desktop only, and only with a real cursor. There is no honest
      // exit signal on touch — a timer just drops a modal over the hero,
      // which is what it was doing. Better to not run at all there.
      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      if (!finePointer.matches || window.innerWidth < 1024) return;

      document.addEventListener('mouseout', function (e) {
        if (!armed()) return;
        if (e.clientY > 0 || e.relatedTarget || e.target.closest('select')) return;
        open();
      });

      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-if-exit-close]') || e.target === modal) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ── 5. Promo bar height ──────────────────────────────────── */

  // The navbar is position:fixed; top:0, so it would sit on top of the
  // bar. Publish the bar's real height and let CSS push the nav down.
  function initPromoOffset() {
    var bar = document.querySelector('.if-promobar');
    var grp0 = document.querySelector('#header-group');
    if (!bar) {
      document.documentElement.style.setProperty('--if-pb-h', '0px');
      if (grp0) document.documentElement.style.setProperty('--if-header-h', grp0.offsetHeight + 'px');
      return;
    }
    function measure() {
      document.documentElement.style.setProperty('--if-pb-h', bar.offsetHeight + 'px');
      // The whole header group is fixed on the homepage, so the page needs
      // padding equal to its real height (bar + nav, wrapping included).
      var grp = document.querySelector('#header-group');
      if (grp) {
        document.documentElement.style.setProperty('--if-header-h', grp.offsetHeight + 'px');
      }
      // navbar.liquid repositions on scroll; nudge it now that the bar's
      // real height is known (fonts and wrapping settle late).
      window.dispatchEvent(new Event('scroll'));
    }
    measure();
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(bar);
    // Fonts land late and can change the bar's height.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  /* ── 6. Promo rotator ─────────────────────────────────────── */

  function initRotators(root) {
    var rots = (root || document).querySelectorAll('[data-if-rotate]:not([data-if-bound])');

    rots.forEach(function (rot) {
      rot.setAttribute('data-if-bound', '');
      var items = rot.querySelectorAll('[data-if-rotate-item]');
      if (items.length < 2 || reduced.matches) return;

      var i = 0;
      var every = parseInt(rot.getAttribute('data-if-rotate'), 10) || 4000;
      setInterval(function () {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, every);
    });
  }

  /* ── Boot ─────────────────────────────────────────────────── */

  function boot(root) {
    try {
      initPromoOffset();
      initQuickAdd(root);
      initQuickToggles(root);
      initNotify(root);
      initVideos(root);
      initProduct(root);
      initStickyOffset();
      initPdpPin(root);
      initZoom(root);
      initCardPeek(root);
      initCartDiscount(root);
      initDiscountRemove(root);
      syncDiscounts();
      // Quantity changes and item removals change what a percentage
      // discount is worth, so re-read it whenever the cart re-renders.
      if (!document.documentElement.hasAttribute('data-if-disc-bound')) {
        document.documentElement.setAttribute('data-if-disc-bound', '');
        window.addEventListener('kt.cart.reloaded', syncDiscounts);
        document.addEventListener('cart:refresh', syncDiscounts);
      }
      initQuickView();
      initStickyAtc(root);
      initCountdowns(root);
      initStickyCta(root);
      initExitOffer(root);
      initRotators(root);
    } catch (err) {
      if (window.console) console.error('[instinct-first/commerce]', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
